---
title: "What Is The Internet?"
slug: "what-is-the-internet"
excerpt: ""
publishedAt: "2026-05-25"
updatedAt: "2026-05-25"
author: "maya-lestari"
category: "Technology"
tags:
  - web
  - networking
  - history
  - protocols
featuredImage: "https://askleo.com/wp-content/uploads/2018/10/internet.jpg"
status: "published"
draft: false
---

## Infobox

<InfoBox title="Internet">
<ul>
<li><strong>Type:</strong> Global computer network</li>
<li><strong>Founded:</strong> 1960s–1980s (evolutionary)</li>
<li><strong>Developer:</strong> Various (ARPANET, CERN, IETF, etc.)</li>
<li><strong>Protocols:</strong> TCP/IP, HTTP, FTP, SMTP, DNS</li>
<li><strong>Users:</strong> ~5.4 billion (as of 2024)</li>
<li><strong>Domain Name System (DNS):</strong> Hierarchical naming system for devices</li>
<li><strong>Access Methods:</strong> Broadband, Wi-Fi, mobile networks, dial-up</li>
<li><strong>Primary Use:</strong> Information sharing, communication, commerce</li>
</ul>
</InfoBox>

## Overview

The Internet is a decentralized, packet-switched network that connects millions of private, public, academic, business, and government networks worldwide. It operates on a [client–server model](/wiki/Client%E2%80%93server_model "Client–server model"), where users (clients) request and receive data from servers hosting websites, databases, or services. The Internet is not owned by any single entity but is governed by a combination of [technical standards](/wiki/Internet_standards "Internet standards"), organizations like the [Internet Engineering Task Force (IETF)](/wiki/Internet_Engineering_Task_Force "Internet Engineering Task Force"), and [ICANN](/wiki/Internet_Corporation_for_Assigned_Names_and_Numbers "Internet Corporation for Assigned Names and Numbers") for domain management.

At its core, the Internet relies on [IP addresses](/wiki/Internet_Protocol "Internet Protocol") to uniquely identify devices and [packet switching](/wiki/Packet_switching "Packet switching") to transmit data efficiently. The [World Wide Web](/wiki/World_Wide_Web "World Wide Web"), a subset of the Internet, uses [HTTP/HTTPS](/wiki/Hypertext_Transfer_Protocol "Hypertext Transfer Protocol") to enable hyperlinked documents (web pages) accessible via browsers.

## History / Background

### Origins

The Internet's origins trace back to the [ARPANET](/wiki/ARPANET "ARPANET"), a project funded by the U.S. Department of Defense in the late 1960s. Developed by [ARPA](/wiki/Advanced_Research_Projects_Agency "Advanced Research Projects Agency"), ARPANET introduced [packet switching](/wiki/Packet_switching "Packet switching"), a method for breaking data into small packets for efficient transmission. The first message was sent between two computers at [Stanford](/wiki/Stanford_University "Stanford University") and [UCLA](/wiki/University_of_California,_Los_Angeles "University of California, Los Angeles") on **October 29, 1969**, marking the birth of the Internet.

In the 1970s, [Vinton Cerf](/wiki/Vinton_Cerf "Vinton Cerf") and [Robert Kahn](/wiki/Robert_Kahn "Robert Kahn") developed [TCP/IP](/wiki/TCP/IP "TCP/IP"), the foundational protocol suite that standardized communication across diverse networks. By 1983, ARPANET fully adopted TCP/IP, and the term "*Internet*" began to emerge.

### Expansion and Commercialization

The 1980s and 1990s saw rapid expansion. In 1989, [Tim Berners-Lee](/wiki/Tim_Berners-Lee "Tim Berners-Lee") invented the [World Wide Web](/wiki/World_Wide_Web "World Wide Web") at [CERN](/wiki/CERN "CERN"), introducing [hypertext](/wiki/Hypertext "Hypertext") and the first web browser. The first website, [info.cern.ch](https://info.cern.ch), went live in 1991. The mid-1990s marked the Internet's commercialization, with companies like [Amazon](/wiki/Amazon_(company) "Amazon (company)") and [Google](/wiki/Google "Google") launching in 1994 and 1998, respectively.

Broadband internet replaced dial-up in the 2000s, enabling faster access and the rise of [social media platforms](/wiki/Social_media "Social media") like [Facebook](/wiki/Facebook "Facebook") (2004) and [YouTube](/wiki/YouTube "YouTube") (2005). The proliferation of [smartphones](/wiki/Smartphone "Smartphone") in the 2010s further democratized access, with over **5 billion** users globally by 2024.

## How It Works

### Technical Infrastructure

The Internet relies on a layered architecture, with each layer handling specific functions:

- **Physical Layer:** Includes cables (fiber optics, copper), wireless signals (Wi-Fi, 5G), and hardware like routers and switches.
- **Data Link Layer:** Manages local network communication (e.g., Ethernet, Wi-Fi standards).
- **Network Layer:** Uses [IP addresses](/wiki/IP_address "IP address") to route data packets across networks via [IP](/wiki/IP "IP").
- **Transport Layer:** Ensures reliable data delivery using [TCP](/wiki/Transmission_Control_Protocol "Transmission Control Protocol") (for accuracy) or [UDP](/wiki/User_Datagram_Protocol "User Datagram Protocol") (for speed).
- **Application Layer:** Provides protocols for user-facing services, such as [HTTP](/wiki/Hypertext_Transfer_Protocol "Hypertext Transfer Protocol") (web), [FTP](/wiki/File_Transfer_Protocol "File Transfer Protocol") (file transfer), and [SMTP](/wiki/Simple_Mail_Transfer_Protocol "Simple Mail Transfer Protocol") (email).

### Data Transmission

When a user requests a webpage, the process involves:

1. **DNS Lookup:** The browser queries a [DNS server](/wiki/Domain_Name_System "Domain Name System") to translate a domain name (e.g., `example.com`) into an IP address.
2. **TCP Handshake:** The browser and server establish a connection using TCP to ensure reliable data transfer.
3. **HTTP Request:** The browser sends an HTTP request to the server, which processes it and returns the requested data (e.g., HTML, CSS, JavaScript).
4. **Rendering:** The browser interprets the data and displays the webpage.

Data travels through multiple [backbone networks](/wiki/Internet_backbone "Internet backbone"), often owned by [ISPs](/wiki/Internet_service_provider "Internet service provider") like [AT&amp;T](/wiki/AT%26T "AT&T") or [Level 3](/wiki/Level_3_Communications "Level 3 Communications"), which interconnect at [IXPs](/wiki/Internet_exchange_point "Internet exchange point").

## Important Facts

<FactList>
<ul>
<li><strong>Scale:</strong> The Internet comprises over <strong>1.1 billion</strong> websites and <strong>300 million</strong> active domains (as of 2024).</li>
<li><strong>Speed:</strong> The fastest internet speeds exceed <strong>10 Gbps</strong> (gigabits per second) in some regions, while global average broadband speed is ~110 Mbps.</li>
<li><strong>Languages:</strong> Over <strong>50%</strong> of web content is in English, followed by Chinese, Spanish, and Hindi.</li>
<li><strong>Traffic:</strong> Video streaming (e.g., Netflix, YouTube) accounts for <strong>60%</strong> of global internet traffic.</li>
<li><strong>Security:</strong> Cybercrime costs the global economy <strong>$8 trillion annually</strong>, with phishing and ransomware as top threats.</li>
<li><strong>IoT:</strong> The [Internet of Things (IoT)](/wiki/Internet_of_things "Internet of things") connects over <strong>15 billion</strong> devices, from smart thermostats to industrial sensors.</li>
<li><strong>Email:</strong> Over <strong>300 billion</strong> emails are sent daily, with spam constituting ~50% of total traffic.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "1969", title: "First ARPANET message sent", description: "First ARPANET message sent between UCLA and Stanford." },
  { date: "1971", title: "First email sent by", description: "First email sent by Ray Tomlinson." },
  { date: "1974", title: "TCP/IP protocols introduced by", description: "TCP/IP protocols introduced by Vinton Cerf and Robert Kahn." },
  { date: "1983", title: "ARPANET fully adopts TCP/IP", description: "ARPANET fully adopts TCP/IP; term \"Internet\" gains traction." },
  { date: "1989", title: "Tim Berners-Lee invents the", description: "Tim Berners-Lee invents the World Wide Web at CERN." },
  { date: "1991", title: "First website (info.cern.ch) g", description: "First website ([info.cern.ch](https://info.cern.ch)) goes live." },
  { date: "1995", title: "Amazon and eBay launch", description: "Amazon and eBay launch; JavaScript introduced." },
  { date: "2004", title: "Facebook is founded; Web", description: "Facebook is founded; Web 2.0 era begins." },
  { date: "2005", title: "YouTube is launched", description: "YouTube is launched." },
  { date: "2010", title: "Mobile internet surpasses desk", description: "Mobile internet surpasses desktop traffic." },
  { date: "2016", title: "IPv6 adoption surpasses 10%", description: "IPv6 adoption surpasses 10% globally." },
  { date: "2020", title: "Global internet users exceed", description: "Global internet users exceed 4.5 billion during the COVID-19 pandemic." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li><strong>World Wide Web (WWW)</strong> – A system of interlinked hypertext documents accessed via the Internet.</li>
<li><strong>Intranet</strong> – A private network for organizational use.</li>
<li><strong>Extranet</strong> – A controlled private network for external partners.</li>
<li><strong>Deep web</strong> – Unindexed parts of the Internet (e.g., private databases).</li>
<li><strong>Dark web</strong> – Encrypted networks (e.g., Tor) for anonymity.</li>
<li><strong>Cloud computing</strong> – Delivery of computing services over the Internet.</li>
<li><strong>Net neutrality</strong> – Principle of equal treatment of all internet traffic.</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>What is the difference between the Internet and the World Wide Web?</summary>
<p>The Internet is the global network infrastructure, while the World Wide Web is a service that runs on it, using HTTP to access web pages.</p>
</details>
<details>
<summary>How is an IP address different from a domain name?</summary>
<p>An IP address is a numerical label (e.g., 192.0.2.1) that identifies a device, while a domain name (e.g., `example.com`) is a human-readable alias resolved via DNS.</p>
</details>
<details>
<summary>What is the role of ISPs?</summary>
<p>[Internet Service Providers (ISPs)](/wiki/Internet_service_provider "Internet service provider") provide users with access to the Internet, often via broadband, fiber, or mobile networks.</p>
</details>
<details>
<summary>How does Wi-Fi work?</summary>
<p>Wi-Fi uses [radio waves](/wiki/Radio_frequency "Radio frequency") to transmit data between devices and a router, which connects to the Internet via an ISP.</p>
</details>
<details>
<summary>What is net neutrality?</summary>
<p>Net neutrality is the principle that ISPs must treat all internet traffic equally, without favoring or blocking specific content or services.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Jump up ^ Leiner, Barry M.; Cerf, Vinton G.; Clark, David D.; Kahn, Robert E.; Kleinrock, Leonard; Lynch, Daniel C.; Postel, Jon; Roberts, Larry G.; Wolff, Stephen (2009). "A Brief History of the Internet". *Internet Society*. Archived from the original on 2010-12-04. Retrieved 2024-05-15.</li>
<li>Jump up ^ Cerf, Vinton G.; Kahn, Robert E. (May 1974). "A Protocol for Packet Network Intercommunication". *IEEE Transactions on Communications*. 22 (5): 637–648. doi:10.1109/TCOM.1974.1092259. S2CID 17414700.</li>
<li>Jump up ^ Berners-Lee, Tim; Cailliau, Robert (December 1997). "WorldWideWeb: Proposal for a HyperText Project". *CERN*. Archived from the original on 2014-06-05. Retrieved 2024-05-15.</li>
<li>Jump up ^ Statista. "Number of internet users worldwide from 2005 to 2024". Archived from the original on 2024-01-18. Retrieved 2024-05-15.</li>
<li>Jump up ^ Google. "IPv6 Statistics". Archived from the original on 2024-04-20. Retrieved 2024-05-15.</li>
</ol>
</ReferenceList>

